import {
    AdvancedMarker,
    InfoWindow,
    useAdvancedMarkerRef,
} from '@vis.gl/react-google-maps';
import React, { useState } from 'react';
import {
    BaseExperienceCharge,
    FormValues as Experience,
} from '../../../types/experience';
import {
    Box,
    IconButton,
    Paper,
    Stack,
    SxProps,
    Typography,
    useTheme,
} from '@mui/material';
import {
    Clear,
    Home,
    Business,
    Sailing,
    LocalActivity,
    DirectionsCar,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { capitalize } from 'lodash';
import { ExperienceType } from '../../../enums/experience';
import LiveLocationCircle from './LiveLocationCircle';

export const EXPERIENCE_TYPE_ICON = (
    experienceType: string,
    sx: SxProps,
): React.ReactNode => {
    const map: Record<string, React.ReactNode> = {
        [ExperienceType.Property]: <Home sx={sx} />,
        [ExperienceType.Commercial]: <Business sx={sx} />,
        [ExperienceType.Boat]: <Sailing sx={sx} />,
        [ExperienceType.Activity]: <LocalActivity sx={sx} />,
        [ExperienceType.Vehicle]: <DirectionsCar sx={sx} />,
    };

    return map[experienceType];
};

interface MarkerProps {
    experience: Experience;
}

const MapMarker = ({ experience }: MarkerProps) => {
    const [markerRef, marker] = useAdvancedMarkerRef();
    const [infoWindowOpen, setInfoWindowOpen] = useState(false);
    const theme = useTheme();

    const navigate = useNavigate();

    const handleNavigate = () => {
        navigate(`/viewExperiences/${experience.type}/${experience.id}`);
    };

    const handleClick = (e: google.maps.MapMouseEvent) => {
        setInfoWindowOpen(!infoWindowOpen);
    };

    const getPrice = () => {
        if (experience.charges.length === 0) return `$---`;

        let commercialPrice = 0;

        if (experience.type === ExperienceType.Commercial) {
            const prices = experience.charges.filter(
                (charge) => charge.chargeType === 'price',
            ) as BaseExperienceCharge[];

            commercialPrice = prices.sort(
                (a, b) => a.unitPrice - b.unitPrice,
            )[0]?.unitPrice;
        }

        const price = experience.charges.reduce((acc, charge) => {
            if (charge.chargeType === 'flexiblePrice') {
                return acc + charge.dayPrice;
            }
            if (charge.chargeType === 'cleaningFee') {
                if (charge.cleaningFeeType === 'flat') {
                    return charge?.price;
                }

                return charge?.basePrice;
            }

            if (
                experience.type === ExperienceType.Commercial &&
                charge.chargeType === 'price'
            ) {
                return acc;
            }

            return acc + charge.unitPrice;
        }, commercialPrice);

        return `$${price}`;
    };

    const isLive = !!experience?.lastSeenAt?.lat;

    const position = isLive
        ? {
              lat: experience.lastSeenAt!.lat,
              lng: experience.lastSeenAt!.lng,
          }
        : {
              lat: experience.lat,
              lng: experience.lng,
          };

    if (!position) return null;

    return (
        <>
            <AdvancedMarker
                ref={markerRef}
                position={position}
                onClick={handleClick}
            >
                <Paper
                    className='marker-animate'
                    sx={{
                        boxShadow: 12,
                        px: 1,
                        py: 0.5,
                        backgroundColor: infoWindowOpen
                            ? 'custom.cardActionBackground'
                            : theme.palette.neutral.main,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                        borderRadius: 2,
                        position: 'relative',
                    }}
                >
                    {isLive && (
                        <Box
                            sx={{
                                position: 'absolute',
                                top: -4,
                                right: -4,
                            }}
                        >
                            <LiveLocationCircle />
                        </Box>
                    )}
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            color: 'text.secondary',
                        }}
                    >
                        {EXPERIENCE_TYPE_ICON(experience.type, {
                            fontSize: '14px',
                        })}
                    </Box>
                    <Typography
                        sx={{
                            fontWeight: 'bold',
                            fontSize: '12px',
                            color: 'text.primary',
                            lineHeight: 1,
                        }}
                    >
                        {getPrice()}
                    </Typography>
                </Paper>
            </AdvancedMarker>
            {infoWindowOpen && (
                <InfoWindow
                    anchor={marker}
                    onClose={() => setInfoWindowOpen(false)}
                    headerDisabled
                >
                    <Stack
                        onClick={handleNavigate}
                        sx={{ cursor: 'pointer', position: 'relative' }}
                    >
                        <img
                            src={experience.photos[0].src}
                            width='100%'
                            height={125}
                            style={{ objectFit: 'cover' }}
                        />
                        <IconButton
                            sx={{
                                position: 'absolute',
                                backgroundColor: theme.palette.neutral.main,
                                boxShadow: 1,
                                m: 1,
                                top: 0,
                                right: 0,
                            }}
                            size='small'
                            onClick={(e) => {
                                e.stopPropagation();
                                setInfoWindowOpen(false);
                            }}
                        >
                            <Clear fontSize='small' />
                        </IconButton>
                        <Box sx={{ p: 2 }}>
                            <Typography sx={{ fontWeight: 'bold' }}>
                                {experience.name}
                            </Typography>
                            <Typography gutterBottom>
                                {capitalize(experience.details?.subType)} in{' '}
                                {experience.suburb}, {experience.state}
                            </Typography>
                            <Typography sx={{ fontWeight: 'bold' }}>
                                From A{getPrice()}+
                            </Typography>
                        </Box>
                    </Stack>
                </InfoWindow>
            )}
        </>
    );
};

export default MapMarker;
